import { Divider, Headline, IconButton, Text, TextButton, TextLink } from '@oneaudi/unified-web-components';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { FsagMonthlyPriceRateDialog } from '~/app/compositions/content/calculation-info/components/fsag-finance-dialog';

import {
	ButtonsStyle,
	HeaderStyle,
	OverviewStyle,
	PriceDetailsStyle,
} from '~/app/compositions/content/service-footer/components/price-details/price-details-style';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { StockCarEnabled, useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { useCtxConfigurator } from '~/app/pages/configurator/context/ctx-configurator';
import { usePriceDetailRemarks } from '~/app/pages/summary/dialogs/price-detail';
import { PriceSection } from '~/app/pages/summary/dialogs/price-detail/components/price-section';
import { RateSection } from '~/app/pages/summary/dialogs/price-detail/components/rate-section';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useIframeDialog } from '~/components/dialog/iframe-dialog/iframe-dialog';
import { FootnoteRenderer, useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';

export const PriceDetailsLegalNl = () => {
	const { setOpenPriceDetails } = useCtxConfigurator();
	const { i18n } = useI18n();
	return (
		<PriceDetailsStyle>
			<div className="close-button">
				<IconButton
					variant={'tertiary'}
					icon={'arrow-down'}
					size={'md'}
					onClick={() => setOpenPriceDetails(false)}
					aria-label={i18n('ngc.actions.showPriceDetails')}
				/>
			</div>
			<div className="content">
				<Header />
				<Overview />
				<FootnoteRenderer />
				<Buttons />
			</div>
		</PriceDetailsStyle>
	);
};

const Header = () => {
	const { config } = useMarketConfig();
	const { catalog } = useConfigurator();
	const { getReference } = useFootnotes();

	if (config.showCaESummaryText && !config.hideConsumptionEmissionEngineName) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	return (
		<HeaderStyle>
			<Headline tag="h2" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
				{catalog.carInfo?.carline}
			</Headline>
			<Text tag="p" variant={{ type: 'copy', order: '1', style: 'normal' }}>
				{catalog.carInfo?.model}
				{!config.hideConsumptionEmissionEngineName && (
					<FootnoteReferenceNgc reference={getReference(CAE_CONFIGUREDCAR_REMARK)} />
				)}
			</Text>
		</HeaderStyle>
	);
};

const Overview = () => {
	const { catalog, carIdentifier } = useConfigurator();
	const { config } = useMarketConfig();
	const { i18n, i18nKeys } = useI18n();
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();
	const { openDialog, closeDialog, isDialogOpen } = useDialogs();
	const financialProducts = useAtomValue(state.financialProducts);
	const selectedProductSettings = useAtomValue(state.fsagFinancialCalculatorSettings);
	const { priceDateRemark } = usePriceDetailRemarks(config.useStaticPriceDateRemark);
	let product: string;
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const { showIframeDialog } = useIframeDialog();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);
	const monthlyRate = catalog.priceSummary?.monthlyRate;

	if (config.showCaESummaryText) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	// price order according to the market, total price is rotr.
	const priceOrder = ['model', 'options', 'vat', 'divider', 'total', 'rotrRate', 'divider', 'rotr'];

	const prices = priceSummary?.parts || [];

	if (selectedProductSettings) {
		product = selectedProductSettings['@ID'] ?? '';
	} else {
		product = defaultProduct ?? '';
	}

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	return (
		<OverviewStyle>
			<Headline tag="h2" variant={{ type: 'headline', order: '4', weight: 'bold' }}>
				{i18n('ngc.priceDetails.title')}
			</Headline>
			<div className="prices">
				{priceOrder.map((price) => {
					return price === 'divider' ? (
						<Divider key={price} />
					) : (
						<PriceSection
							key={price}
							pricePartType={price}
							title={i18n(`ngc.priceDetails.${price}` as keyof typeof i18nKeys)}
							price={formatCurrency(
								prices.find((part) => part.type === price)?.price,
								vatConfig.priceParts[price],
							)}
							disclaimer={price === 'rotr' ? priceDisclaimerText : null}
							bold={price === 'rotr'}
						/>
					);
				})}
			</div>
			{catalog?.priceSummary?.monthlyRate?.__typename === 'MonthlyRate' && config.enableFinancingServices && (
				<div className="rates">
					<RateSection
						pricePartType="totalRate"
						title={
							financialProducts.find((prod) => {
								return prod.key === product;
							})?.val ?? i18n('ngc.priceDetails.privateLeasing')
						}
						price={formatMonthlyRate(catalog.priceSummary?.monthlyRate)}
						bold
					/>
					<TextLink
						variant="standalone"
						size="sm"
						onClick={() => {
							closeDialog('priceDetails');
							appTrackingService.summaryClickEvent({
								eventAction: EventAction.InternalLink,
								eventName: `configurator (price details) - click to open configurator-financing-options-layer`,
								componentName: 'price-details-layer',
								label: i18n('ngc.actions.changeLeasingRate'),
							});
							openDialog('monthlyPriceRatesFsag');
						}}>
						{i18n('ngc.actions.changeLeasingRate')}
					</TextLink>
					{config.deliveryCostUrl && (
						<TextLink
							variant="standalone"
							onClick={() => {
								config.useIframe
									? () =>
											showIframeDialog({
												url: config.deliveryCostUrl,
												name: 'delivery-cost-layer',
											})
									: () => window.open(config.specialOfferUrl);
							}}
							size="sm">
							{i18n('ngc.priceDetails.deliveryCosts')}
						</TextLink>
					)}
				</div>
			)}
			{priceDateRemark && (
				<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml html={priceDateRemark} />
				</Text>
			)}
			{monthlyRate?.__typename === 'MonthlyRate' && config.enableFinancingServices && (
				<div>
					<FsagMonthlyPriceRateDialog
						open={isDialogOpen('monthlyPriceRatesFsag')}
						onClose={() => closeDialog('monthlyPriceRatesFsag')}
					/>
				</div>
			)}
		</OverviewStyle>
	);
};

const Buttons = () => {
	const [, setCurrentView] = useAtom(state.currentView);
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { showStockCarDialog } = useStockCarDialog(config.stockCarConfig.geoFilter);
	const { setOpenPriceDetails } = useCtxConfigurator();

	const handleClickSummary = useCallback(() => {
		const body = document?.getElementsByTagName('body');
		body[0].style.overflow = 'auto';
		setOpenPriceDetails(false);
		setCurrentView('summary');
		appTrackingService.basketNavigationClick({
			label: i18n('ngc.actions.getYourCar'),
			targetUrl: '#summary',
			targetPage: 'summary',
		});
	}, [i18n, setCurrentView]);

	const handleClickStockCar = () => {
		setOpenPriceDetails(false);
		showStockCarDialog();
	};

	return (
		<ButtonsStyle>
			{StockCarEnabled() && (
				<TextButton
					className="main-cta-button"
					type={'button'}
					variant={'secondary'}
					onClick={handleClickStockCar}
					size={'sm'}
					isFullWidth>
					{i18n('ngc.stockcar.trigger')}
				</TextButton>
			)}
			<TextButton href={'#summary'} variant="primary" onClick={handleClickSummary} size="sm">
				{i18n('ngc.actions.getYourCar')}
			</TextButton>
		</ButtonsStyle>
	);
};
